import { useEffect, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-hot-toast';
import { formatInTimezone, shiftStatus, useTimezone } from '@kioskgaming/ui';
import CashierLayout from '../components/CashierLayout';
import DenominationGrid from '../components/DenominationGrid';
import { useDeviceErrorRedirect } from '../hooks/useDeviceErrorRedirect';
import {
  cashierConfirmHandover,
  cashierGetShift,
  cashierInitiateHandover,
  cashierListAssignedShifts,
  CashierShift,
} from '../services/cashierApi';
import { emptyDenominationGrid } from '../utils/denominations';
import { canJoinShift } from '../utils/shiftAccess';

type HandoverMode = 'initiate' | 'confirm' | 'none';

export default function HandoverPage() {
  const { id } = useParams<{ id: string }>();
  const { timezone } = useTimezone();
  const navigate = useNavigate();
  const handleDeviceError = useDeviceErrorRedirect();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [mode, setMode] = useState<HandoverMode>('none');
  const [shift, setShift] = useState<CashierShift | null>(null);
  const [assignedShifts, setAssignedShifts] = useState<CashierShift[]>([]);
  const [toShiftId, setToShiftId] = useState('');
  const [denominations, setDenominations] = useState(emptyDenominationGrid());

  useEffect(() => {
    if (!id) return;
    const load = async () => {
      setLoading(true);
      try {
        const [shiftRes, assignedRes] = await Promise.all([
          cashierGetShift(id),
          cashierListAssignedShifts(),
        ]);
        if (!shiftRes.success || !shiftRes.data?.shift) {
          toast.error(shiftRes.message || 'Shift not found');
          navigate('/', { replace: true });
          return;
        }
        const current = shiftRes.data.shift;
        setShift(current);
        // Error payloads may carry `data` too, so only read the list from a successful response.
        const others = (assignedRes.success ? assignedRes.data || [] : []).filter((s) => s.id !== id);
        setAssignedShifts(others);

        if (canJoinShift(current.status)) {
          setMode('initiate');
        } else if (current.status === 'scheduled') {
          setMode('confirm');
        } else {
          setMode('none');
        }
      } finally {
        setLoading(false);
      }
    };
    void load();
  }, [id, navigate]);

  if (!id) return <Navigate to="/" replace />;

  const onInitiate = async () => {
    if (!toShiftId) {
      toast.error('Select the incoming shift');
      return;
    }
    const items = denominations.filter((d) => d.quantity > 0);
    if (items.length === 0) {
      toast.error('Enter at least one denomination');
      return;
    }
    setSubmitting(true);
    try {
      const res = await cashierInitiateHandover(id, { toShiftId, denominations: items });
      if (!res.success) {
        if (handleDeviceError(res, `/shifts/${id}`)) return;
        toast.error(res.message || 'Handover failed');
        return;
      }
      toast.success('Handover initiated');
      navigate(`/shifts/${id}`, { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  const onConfirm = async () => {
    const items = denominations.filter((d) => d.quantity > 0);
    if (items.length === 0) {
      toast.error('Enter at least one denomination');
      return;
    }
    setSubmitting(true);
    try {
      const res = await cashierConfirmHandover(id, { denominations: items });
      if (!res.success) {
        // Confirm runs on the till being handed over; on another device go back to My shifts.
        if (handleDeviceError(res, '/')) return;
        toast.error(res.message || 'Confirm handover failed');
        return;
      }
      toast.success('Handover confirmed');
      navigate(`/shifts/${id}/opening`, { replace: true });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <CashierLayout
      title={mode === 'confirm' ? 'Confirm handover' : 'Initiate handover'}
      subtitle={shift?.status ? `Shift status: ${shiftStatus(shift.status).label}` : undefined}
      backTo={`/shifts/${id}`}
    >
      {loading ? (
        <div className="text-sm text-stone-600">Loading…</div>
      ) : mode === 'none' ? (
        <div className="rounded-xl border border-stone-200 bg-white p-4 text-sm text-stone-600">
          Handover is not available for this shift status.
        </div>
      ) : (
        <div className="rounded-xl border border-stone-200 bg-white p-4 sm:p-6">
          {mode === 'initiate' ? (
            <>
              <p className="text-sm text-stone-600">
                Count cash to hand over to the next Cashier. Select their scheduled shift below.
              </p>
              <div className="mt-4">
                <label className="block text-sm font-medium text-stone-700 mb-1">Incoming shift</label>
                <select
                  value={toShiftId}
                  onChange={(e) => setToShiftId(e.target.value)}
                  className="w-full rounded-lg border border-stone-200 px-3 py-2 text-sm outline-none focus:border-emerald-500"
                  disabled={submitting}
                >
                  <option value="">Select shift…</option>
                  {assignedShifts
                    .filter((s) => s.status === 'scheduled')
                    .map((s) => (
                      <option key={s.id} value={s.id}>
                        {s.scheduledStartAt
                          ? formatInTimezone(s.scheduledStartAt, timezone)
                          : s.id}
                      </option>
                    ))}
                </select>
              </div>
            </>
          ) : (
            <p className="text-sm text-stone-600">
              Count the cash received from the previous Cashier to confirm handover.
            </p>
          )}

          <div className="mt-4">
            <DenominationGrid value={denominations} onChange={setDenominations} disabled={submitting} />
          </div>

          <button
            type="button"
            onClick={() => void (mode === 'confirm' ? onConfirm() : onInitiate())}
            disabled={submitting}
            className="mt-6 w-full rounded-lg bg-emerald-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-emerald-700 disabled:opacity-60"
          >
            {submitting
              ? 'Submitting…'
              : mode === 'confirm'
                ? 'Confirm handover'
                : 'Initiate handover'}
          </button>
        </div>
      )}
    </CashierLayout>
  );
}
